import React, {useEffect, useMemo, useState} from "react";
import {Box, ButtonBase, Stack, Typography} from "@mui/material";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import {ChartPerson, ChartUnit, EmpOrganizationRestControllerApiFactory, OrganizationChart} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {envVars} from "../../../components/utils/env_vars";
import {EmptyState, fullName, Panel, PersonAvatar, SkeletonRows, SoftChip} from "../../../components/kit";
import {MANAGEMENT_ROLES, PersonRole, RoleBadges, rolesByPerson} from "../../../components/layout/shared/management_roles";

const photoOf = (p: ChartPerson) => envVars.apiBasePath + "/pub/team/photo/" + p.id + "?width=200";

interface ChartContext {
	roles: Map<number, PersonRole[]>;
	children: Map<number | undefined, ChartUnit[]>;
	onSelect?: (personId: number) => void;
}

const PersonRow = ({p, ctx, caption}: { p: ChartPerson, ctx: ChartContext, caption?: string }) => (
	<ButtonBase onClick={() => p.id && ctx.onSelect?.(p.id)} disabled={!ctx.onSelect}
				sx={{justifyContent: "flex-start", textAlign: "left", borderRadius: 2, p: 1, width: "100%", "&:hover": {bgcolor: "action.hover"}}}>
		<Stack direction="row" spacing={1.25} alignItems="center" sx={{minWidth: 0, width: "100%"}}>
			<PersonAvatar name={fullName(p)} src={photoOf(p)} size={36}/>
			<Box sx={{minWidth: 0}}>
				{caption && <Typography variant="caption" color="text.secondary" display="block" sx={{lineHeight: 1.2}}>{caption}</Typography>}
				<Typography variant="body2" fontWeight={600} sx={{overflowWrap: "anywhere"}}>{fullName(p)}</Typography>
				{p.id && ctx.roles.get(p.id) && <Box sx={{mt: 0.5}}><RoleBadges roles={ctx.roles.get(p.id)}/></Box>}
			</Box>
		</Stack>
	</ButtonBase>
);

/** Una unidad con su responsable y sus miembros, y debajo, sangradas, sus unidades hijas. */
const UnitNode = ({unit, ctx, depth}: { unit: ChartUnit, ctx: ChartContext, depth: number }) => {
	const members = unit.members || [];
	const kids = ctx.children.get(unit.id) || [];
	const empty = members.length === 0 && !unit.manager;
	return (
		<Box>
			<Box sx={{p: {xs: 1.5, sm: 2}, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: "background.paper",
				borderStyle: empty ? "dashed" : "solid"}}>
				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: unit.description ? 0.25 : 1}}>
					<AccountTreeRoundedIcon fontSize="small" color={depth === 0 ? "primary" : "action"}/>
					<Typography variant="subtitle1" component="h3" fontWeight={700}>{unit.name}</Typography>
					{members.length > 0
						? <SoftChip tone="neutral" label={members.length === 1 ? "1 persona" : `${members.length} personas`}/>
						: <SoftChip tone="warning" label="Sin miembros"/>}
				</Stack>
				{unit.description && <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>{unit.description}</Typography>}
				{unit.manager
					? <PersonRow p={unit.manager} ctx={ctx} caption="Responsable"/>
					: <Typography variant="body2" color="text.secondary" sx={{px: 1, py: 0.5}}>Sin responsable asignado</Typography>}
				{members.length > 0 && (
					<Box sx={{
						mt: 1, pt: 1, borderTop: "1px solid", borderColor: "divider", display: "grid", gap: 0.5,
						gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))"},
					}}>
						{members.map(m => <PersonRow key={m.id} p={m} ctx={ctx}/>)}
					</Box>
				)}
			</Box>
			{kids.length > 0 && (
				<Stack spacing={2} sx={{mt: 2, ml: {xs: 1, sm: 3}, pl: {xs: 1.5, sm: 2}, borderLeft: "2px solid", borderColor: "divider"}}>
					{kids.map(k => <UnitNode key={k.id} unit={k} ctx={ctx} depth={depth + 1}/>)}
				</Stack>
			)}
		</Box>
	);
};

/** Quién tiene hoy cada rol del sistema de gestión, con los vacantes marcados. */
const RolesPanel = ({chart}: { chart: OrganizationChart }) => {
	const byRole = new Map((chart.roles || []).map(r => [r.role as string, r]));
	return (
		<Panel title="Roles del sistema de gestión" icon={<VerifiedUserOutlinedIcon fontSize="small" color="action"/>}
			   subtitle="Quién tiene hoy cada rol de calidad y seguridad de la información." sx={{mt: 3}}>
			<Box sx={{display: "grid", gap: 1.5, gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))"}}}>
				{MANAGEMENT_ROLES.map(r => {
					const v = byRole.get(r.value);
					return (
						<Box key={r.value} sx={{p: 1.5, borderRadius: 2.5, border: "1px solid", borderColor: "divider", minWidth: 0}}>
							<Typography variant="caption" color="text.secondary" display="block">{r.label}</Typography>
							{v?.holder
								? <Typography variant="body2" fontWeight={600}>{fullName(v.holder)}</Typography>
								: <Box sx={{mt: 0.25}}><SoftChip tone="warning" label={v ? "Vacante" : "Sin designar"}/></Box>}
							{v?.deputy && (
								<Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.25}}>
									{v.holder ? "Suplente" : "En funciones"}: {fullName(v.deputy)}
								</Typography>
							)}
						</Box>
					);
				})}
			</Box>
		</Panel>
	);
};

/**
 * Organigrama de la plantilla (#13897): árbol de unidades organizativas con su responsable, sus
 * miembros vigentes y los roles del sistema de gestión de cada persona (#13896). Solo lectura.
 */
export const OrgChartView = ({onSelect}: { onSelect?: (personId: number) => void }) => {
	const apiManager = useApiManager();
	const [chart, setChart] = useState<OrganizationChart>();
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		apiManager.execute(apiManager.factory(EmpOrganizationRestControllerApiFactory).organizationChart())
			.then(setChart).catch(() => setFailed(true));
	}, []);

	const ctx = useMemo<ChartContext | undefined>(() => {
		if (!chart) {
			return undefined;
		}
		const units = chart.units || [];
		const ids = new Set(units.map(u => u.id));
		const children = new Map<number | undefined, ChartUnit[]>();
		units.forEach(u => {
			const parent = u.parentId && ids.has(u.parentId) ? u.parentId : undefined;
			children.set(parent, [...(children.get(parent) || []), u]);
		});
		return {roles: rolesByPerson(chart.roles), children, onSelect};
	}, [chart, onSelect]);

	if (failed) {
		return <EmptyState icon={<AccountTreeRoundedIcon/>} title="No se ha podido cargar el organigrama" description="Vuelve a intentarlo en un rato."/>;
	}
	if (!chart || !ctx) {
		return <SkeletonRows rows={3} height={120}/>;
	}
	const roots = ctx.children.get(undefined) || [];
	return (
		<>
			{roots.length === 0 ? (
				<EmptyState icon={<AccountTreeRoundedIcon/>} title="Todavía no hay unidades organizativas"
							description="Las da de alta RR. HH. desde la administración de personal."/>
			) : (
				<Stack spacing={2}>
					{roots.map(u => <UnitNode key={u.id} unit={u} ctx={ctx} depth={0}/>)}
				</Stack>
			)}
			<RolesPanel chart={chart}/>
		</>
	);
};
